import { Form } from 'antd';
import { ProductionItemDetail } from 'constants/interface';
import { buildProductionItemQuantityDisplay } from 'helpers/order';
import React, { useState } from 'react';
import { useDispatch } from 'hooks/store';
import {
  Input,
  Modal,
} from 'UI';
import { createSeedTicketAction } from 'state/slices/productionItem';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { unwrapResult } from '@reduxjs/toolkit';

import {
  InlineButton,
  MainContainer,
  FormContainer,
  ItemInfoContainer,
  ItemInfoRow,
  ItemInfoLabel,
  ItemInfoValue,
  BaseFormItem,
  FormItemRow,
} from './style';

interface SeedTicketModalProps {
  item: ProductionItemDetail
  isVisible: boolean
  onCancel: () => void
  onSuccess: () => void
}

interface SeedTicketFormData {
  requestQuantity: string
}

const SeedTicketModal = ({
  item,
  isVisible,
  onCancel,
  onSuccess,
}: SeedTicketModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);

  const [seedTicketForm] = Form.useForm<SeedTicketFormData>();

  const createSeedTicket = async (): Promise<void> => {
    try {
      await seedTicketForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }
    const formData = seedTicketForm.getFieldsValue();

    const parsedData = {
      requestQuantity: parseInt(formData.requestQuantity, 10),
    };

    try {
      setLoading(true);
      const result = await dispatch(createSeedTicketAction({
        productionItemId: item.id,
        data: parsedData,
      }));
      setLoading(false);
      unwrapResult(result);
      onSuccess();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar requisitar semente. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Programação de semeio"
      destroyOnClose
      onCancel={onCancel}
      afterClose={onCancel}
      zIndex={90}
      footer={[
        <InlineButton
          key="cancel"
          onClick={onCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={createSeedTicket}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <MainContainer>
        <ItemInfoContainer>
          <ItemInfoRow>
            <ItemInfoLabel>Cliente:</ItemInfoLabel>
            <ItemInfoValue>{`${item.orderItem.order.client.name}`}</ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Cultivar:</ItemInfoLabel>
            <ItemInfoValue>{`${item.product.variety.culture.name} ${item.product.variety.name}`}</ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Quantidade prevista:</ItemInfoLabel>
            <ItemInfoValue>
              {`${buildProductionItemQuantityDisplay(item)}`}
            </ItemInfoValue>
          </ItemInfoRow>
        </ItemInfoContainer>
        <FormContainer
          layout="vertical"
          form={seedTicketForm}
          requiredMark={false}
          validateTrigger="onSubmit"
          initialValues={{
            requestQuantity: item.calculatedQuantity,
          }}
        >
          <FormItemRow>
            <BaseFormItem
              label="Quantidade solicitada"
              name="requestQuantity"
              rules={[
                {
                  required: true,
                  message: 'Preencha uma quantidade',
                },
              ]}
            >
              <Input />
            </BaseFormItem>
          </FormItemRow>
        </FormContainer>
      </MainContainer>
    </Modal>
  );
};

export default SeedTicketModal;
